import { useEffect, useState } from 'react'

// "The on-screen keyboard is (probably) up": an editable control has focus, or
// the visual viewport has shrunk well below the layout viewport. The floating
// Scan button (ScanFab) hides on either, so it never sits over a composer's
// Send button or a field right above the keyboard (the Judge on /rules).

/** Inputs that never raise a text keyboard. */
const NON_TEXT_INPUTS = new Set(['checkbox', 'radio', 'button', 'submit', 'reset', 'image', 'file', 'range', 'color', 'hidden'])

/** A shrink beyond this (px) between layout and visual viewport is a keyboard. */
export const KEYBOARD_SHRINK_PX = 150

/** True for a control that takes typing: text inputs, textarea, select, contenteditable. */
export function isEditableTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false
  const tag = el.tagName
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (tag === 'INPUT') return !NON_TEXT_INPUTS.has((el as HTMLInputElement).type)
  if (el.isContentEditable) return true
  // jsdom has no isContentEditable; the attribute is the fallback.
  // eslint-disable-next-line i18next/no-literal-string -- CSS selector
  return !!el.closest('[contenteditable=""], [contenteditable="true"]')
}

function viewportShrunk(): boolean {
  if (typeof window === 'undefined' || !window.visualViewport) return false
  return window.innerHeight - window.visualViewport.height > KEYBOARD_SHRINK_PX
}

export function useOnScreenKeyboard(): boolean {
  const [focused, setFocused] = useState(() => typeof document !== 'undefined' && isEditableTarget(document.activeElement))
  const [shrunk, setShrunk] = useState(viewportShrunk)

  useEffect(() => {
    // focusout's relatedTarget is where focus is GOING (null: nowhere), so
    // moving from one field to another never flickers the button back.
    const onIn = (e: FocusEvent) => setFocused(isEditableTarget(e.target))
    const onOut = (e: FocusEvent) => setFocused(isEditableTarget(e.relatedTarget))
    document.addEventListener('focusin', onIn)
    document.addEventListener('focusout', onOut)
    const vv = window.visualViewport
    const onResize = () => setShrunk(viewportShrunk())
    vv?.addEventListener('resize', onResize)
    window.addEventListener('resize', onResize)
    return () => {
      document.removeEventListener('focusin', onIn)
      document.removeEventListener('focusout', onOut)
      vv?.removeEventListener('resize', onResize)
      window.removeEventListener('resize', onResize)
    }
  }, [])

  return focused || shrunk
}
